import { EVENTS_PATH, StudioEventSchema, type StudioEvent } from '@studio/shared';
import { useEffect, useRef } from 'react';
import { browserTransport, type ActivityTransport } from './activity/index.js';
import { api } from './api.js';

type Listener = (event: StudioEvent) => void;

const listeners = new Set<Listener>();
let source: EventSource | null = null;
let reopening: ReturnType<typeof setTimeout> | null = null;
let backoffMs = 0;
/** Whether there is a session to stream to. The shell is the only thing that knows. */
let sessionOpen = false;

/**
 * How long to wait before opening the stream again after it drops.
 *
 * A stream that has ended does not come back on its own: an EventSource refused by
 * status never retries, and without reopening the stream is dead for the whole session
 * - every surface still renders and nothing on it ever moves. But reopening on a fixed
 * two-second tick is its own fault: a tab left open after signing out would knock on
 * the door until evening. So the stream is only opened while the shell says there is a
 * session, and a connection that keeps dropping backs off to a ceiling.
 */
const REOPEN_MIN_MS = 1000;
const REOPEN_MAX_MS = 30_000;

function deliver(message: MessageEvent<string>): void {
  let payload: unknown;
  try {
    payload = JSON.parse(message.data);
  } catch {
    return;
  }
  const parsed = StudioEventSchema.safeParse(payload);
  if (!parsed.success) return;
  for (const each of [...listeners]) each(parsed.data);
}

/**
 * What a dropped stream cannot tell on its own, asked of the server instead.
 *
 * An EventSource reports that a connection ended and nothing else: a refusal for want
 * of a session and a cable pulled out of the wall arrive as the same bare `error`, and
 * the status that separates them is not readable from script. So the stream does not
 * decide anything - it asks the one client that can be refused out loud, and a 401
 * there closes the shell exactly as a 401 from a surface would.
 *
 * Every other answer, including no answer at all, leaves the session alone: a network
 * fault deserves the reopen below and nothing more. Turning one into a sign-out would
 * be a worse defect than the silence this replaces - the operator would be thrown out
 * of a Workspace that was never closed to them.
 */
function askWhetherTheSessionStands(): void {
  void api.readSession().catch(() => undefined);
}

function stopReopening(): void {
  if (reopening === null) return;
  clearTimeout(reopening);
  reopening = null;
}

function openStream(): void {
  // No session means nothing to stream, and no event stream in this environment means
  // no live updates - neither is a broken shell.
  if (!sessionOpen || source !== null || listeners.size === 0 || typeof EventSource === 'undefined') return;
  const opened = new EventSource(EVENTS_PATH);
  opened.addEventListener('resource', deliver as EventListener);
  opened.onopen = (): void => {
    backoffMs = 0;
  };
  opened.onerror = (): void => {
    opened.close();
    if (source === opened) source = null;
    stopReopening();
    if (!sessionOpen) return;
    askWhetherTheSessionStands();
    backoffMs = backoffMs === 0 ? REOPEN_MIN_MS : Math.min(backoffMs * 2, REOPEN_MAX_MS);
    reopening = setTimeout(() => {
      reopening = null;
      openStream();
    }, backoffMs);
  };
  source = opened;
}

/**
 * What the shell knows and this module cannot see: whether the operator is signed in.
 *
 * Closing it stops the stream and stops trying, which is what turns a signed-out tab
 * from a caller into a quiet one. Opening it starts immediately rather than after a
 * backoff: signing back in is news, not a retry.
 */
export function setStreamSession(open: boolean): void {
  if (sessionOpen === open) return;
  sessionOpen = open;
  if (open) {
    backoffMs = 0;
    openStream();
    return;
  }
  stopReopening();
  if (source !== null) {
    source.close();
    source = null;
  }
}

/**
 * One event stream for the whole shell. The indicator and every open surface read the
 * same connection, so entering a surface never costs a second SSE connection and no
 * surface can see a different ordering of the same events.
 */
export function subscribeToEvents(listener: Listener): () => void {
  listeners.add(listener);
  openStream();
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    stopReopening();
    if (source !== null) {
      source.close();
      source = null;
    }
  };
}

/** The activity indicator rides the shared stream rather than opening its own. */
export function sharedActivityTransport(): ActivityTransport {
  const base = browserTransport();
  return { open: subscribeToEvents, readSnapshot: () => base.readSnapshot() };
}

/** Subscribes for as long as the component is mounted. The callback may change freely. */
export function useResourceEvents(onEvent: Listener): void {
  const held = useRef(onEvent);
  held.current = onEvent;
  useEffect(() => subscribeToEvents((event) => held.current(event)), []);
}
